<template>
  <div class="app-container agent-mobile-page">
    <!-- 概览 -->
    <div class="stat-row">
      <div class="stat-card">
        <div class="stat-card__label">代理号总数</div>
        <div class="stat-card__value">{{ stats.total }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-card__label">冷启动写入</div>
        <div class="stat-card__value">{{ stats.cold_start }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-card__label">运行时识别</div>
        <div class="stat-card__value">{{ stats.runtime }}</div>
      </div>
      <div class="stat-card stat-card--muted">
        <div class="stat-card__label">已停用</div>
        <div class="stat-card__value">{{ stats.disabled }}</div>
      </div>
    </div>

    <!-- 说明 -->
    <el-alert
      type="info"
      :closable="false"
      show-icon
      class="tip"
      title="代理号不参与「同入住人复用同一张卡」的判定"
    >
      <div class="tip__body">
        判定口径：近 3 个自然日内，同一手机号在同一客户下（<b>不分上游渠道，锦江+丽呈合并统计</b>）
        出现 <b>≥3 个不同订单</b> 且 <b>≥3 个不同入住人姓名</b>（不含 TBA 等占位名），判定为代理号。
        入池后永久有效；下单命中后，同入住人复用判定中<b>不再比较手机号</b>，只比较姓名。
      </div>
    </el-alert>

    <!-- 筛选 -->
    <div class="filter-container">
      <el-input
        class="filter-item"
        v-model="listQuery.app_client_name"
        placeholder="下游客户名称"
        clearable
        style="width: 170px"
        size="small"
        @keyup.enter.native="handleFilter"
      />
      <el-input
        class="filter-item"
        v-model="listQuery.app_client_id"
        placeholder="下游渠道 ID"
        clearable
        style="width: 130px"
        size="small"
        @keyup.enter.native="handleFilter"
      />
      <el-input
        class="filter-item"
        v-model="listQuery.mobile"
        placeholder="手机号"
        clearable
        style="width: 150px"
        size="small"
        @keyup.enter.native="handleFilter"
      />
      <el-select
        class="filter-item"
        v-model="listQuery.source"
        placeholder="来源"
        clearable
        style="width: 130px"
        size="small"
      >
        <el-option label="冷启动" value="cold_start" />
        <el-option label="运行时" value="runtime" />
      </el-select>
      <el-select
        class="filter-item"
        v-model="listQuery.status"
        placeholder="状态"
        clearable
        style="width: 120px"
        size="small"
      >
        <el-option label="生效中" :value="1" />
        <el-option label="已停用" :value="0" />
      </el-select>
      <el-date-picker
        class="filter-item"
        v-model="dateArr"
        type="daterange"
        align="right"
        value-format="yyyy-MM-dd"
        unlink-panels
        range-separator="至"
        start-placeholder="触发开始日"
        end-placeholder="触发结束日"
        size="small"
        style="width: 250px"
      />
      <el-button
        class="filter-item"
        icon="el-icon-search"
        :loading="listLoading"
        type="primary"
        size="small"
        @click="handleFilter"
      >搜索</el-button>
      <el-button
        class="filter-item"
        icon="el-icon-refresh-left"
        size="small"
        @click="handleReset"
      >重置</el-button>
    </div>

    <!-- 列表 -->
    <el-table
      v-loading="listLoading"
      :data="list"
      border
      fit
      highlight-current-row
      size="small"
    >
      <el-table-column label="ID" width="80" align="center">
        <template slot-scope="scope">{{ scope.row.id }}</template>
      </el-table-column>

      <el-table-column label="下游客户" min-width="120" align="center">
        <template slot-scope="scope">
          {{ scope.row.app_client_name || '-' }}
        </template>
      </el-table-column>

      <el-table-column label="下游渠道 ID" width="110" align="center">
        <template slot-scope="scope">
          <span class="mono">{{ scope.row.app_client_id }}</span>
        </template>
      </el-table-column>

      <el-table-column label="手机号" min-width="130" align="center">
        <template slot-scope="scope">
          <span class="mono">{{ scope.row.mobile }}</span>
        </template>
      </el-table-column>

      <el-table-column label="触发时" width="150" align="center">
        <template slot-scope="scope">
          <span class="text-muted">{{ scope.row.guest_count }} 人 / {{ scope.row.order_count }} 单</span>
        </template>
      </el-table-column>

      <el-table-column label="触发窗口" min-width="180" align="center">
        <template slot-scope="scope">
          <span class="text-muted">{{ scope.row.window_start }} ~ {{ scope.row.window_end }}</span>
        </template>
      </el-table-column>

      <el-table-column label="来源" width="100" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.source === 'cold_start'" size="mini" type="info">冷启动</el-tag>
          <el-tag v-else size="mini" type="success">运行时</el-tag>
        </template>
      </el-table-column>

      <el-table-column label="最后下单" min-width="150" align="center">
        <template slot-scope="scope">
          <span class="text-muted">{{ scope.row.last_order_at || '-' }}</span>
        </template>
      </el-table-column>

      <el-table-column label="状态" width="90" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.status === 1" size="mini" type="success">生效中</el-tag>
          <el-tag v-else size="mini" type="danger">已停用</el-tag>
        </template>
      </el-table-column>

      <el-table-column label="操作" width="170" align="center" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="mini" @click="handleDetail(scope.row)">详情</el-button>
          <el-button
            v-if="scope.row.status === 1"
            type="text"
            size="mini"
            class="text-danger"
            @click="handleToggle(scope.row, 0)"
          >停用</el-button>
          <el-button
            v-else
            type="text"
            size="mini"
            @click="handleToggle(scope.row, 1)"
          >启用</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-wrap">
      <el-pagination
        background
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="listQuery.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="listQuery.limit"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
      />
    </div>

    <!-- 详情弹窗 -->
    <el-dialog title="代理号详情" :visible.sync="detailVisible" width="620px">
      <el-descriptions v-if="current" :column="2" border size="small">
        <el-descriptions-item label="下游客户">
          {{ current.app_client_name || '-' }}
        </el-descriptions-item>
        <el-descriptions-item label="下游渠道 ID">
          <span class="mono">{{ current.app_client_id }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="手机号">
          <span class="mono">{{ current.mobile }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="来源">
          {{ current.source === 'cold_start' ? '冷启动' : '运行时' }}
        </el-descriptions-item>
        <el-descriptions-item label="触发时姓名数">{{ current.guest_count }} 人</el-descriptions-item>
        <el-descriptions-item label="触发时订单数">{{ current.order_count }} 单</el-descriptions-item>
        <el-descriptions-item label="触发窗口" :span="2">
          {{ current.window_start }} ~ {{ current.window_end }}
        </el-descriptions-item>
        <el-descriptions-item label="识别时间" :span="2">
          {{ current.created_at }}
        </el-descriptions-item>
      </el-descriptions>

      <div class="name-samples">
        <div class="name-samples__title">
          触发时的入住人姓名（前 5 个，用于人工复核是否真代理号）
        </div>
        <div class="name-samples__body">
          <el-tag
            v-for="(n, i) in (current && current.sample_names ? current.sample_names.split(',') : [])"
            :key="i"
            size="small"
            class="name-tag"
          >{{ n }}</el-tag>
          <span v-if="!current || !current.sample_names" class="text-muted">无</span>
        </div>
        <div class="name-samples__hint">
          若这些都是真实的不同客人 → 确认是代理号；<br />
          若其实是同一人不同写法 / 占位名 → 建议停用该条，避免误伤正常复用。
        </div>
      </div>

      <span slot="footer">
        <el-button @click="detailVisible = false" size="small">关闭</el-button>
        <el-button
          v-if="current && current.status === 1"
          type="danger"
          size="small"
          @click="handleToggle(current, 0)"
        >停用此代理号</el-button>
        <el-button
          v-else-if="current"
          type="primary"
          size="small"
          @click="handleToggle(current, 1)"
        >恢复启用</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'

const MOCK = [
  {
    id: 1, app_client_id: 62, app_client_name: '深圳道旅', target_type: 'jin_jiang',
    mobile: '17119243921', guest_count: 8, order_count: 6,
    window_start: '2026-10-13', window_end: '2026-10-15',
    sample_names: 'Aen Ji Ha,Junnie Evarrete,SSU YUNG LIN,Cindy Mae Yu,许璐',
    source: 'cold_start', status: 1,
    last_order_at: '2026-11-03 22:41', created_at: '2026-11-04 01:02:11'
  },
  {
    id: 2, app_client_id: 62, app_client_name: '深圳道旅', target_type: 'jin_jiang',
    mobile: '17112867921', guest_count: 7, order_count: 5,
    window_start: '2026-10-13', window_end: '2026-10-15',
    sample_names: 'SENA CHO,Marika Kvirikashvili,JIHYE CHA,Danim Lee,AYEONG SIN',
    source: 'cold_start', status: 1,
    last_order_at: '2026-11-03 20:13', created_at: '2026-11-04 01:02:11'
  },
  {
    id: 3, app_client_id: 62, app_client_name: '深圳道旅', target_type: 'jin_jiang',
    mobile: '17114974965', guest_count: 6, order_count: 4,
    window_start: '2026-10-14', window_end: '2026-10-16',
    sample_names: 'Duc Vo,Homer Usares,Khristine Jewel Sencil,Arief Ekto Rahman',
    source: 'cold_start', status: 1,
    last_order_at: '2026-11-03 19:58', created_at: '2026-11-04 01:02:11'
  },
  {
    id: 4, app_client_id: 62, app_client_name: '深圳道旅', target_type: 'li_cheng',
    mobile: '17114390056', guest_count: 9, order_count: 7,
    window_start: '2026-10-28', window_end: '2026-10-30',
    sample_names: 'KIM MIYEON,Atiqah rozali,Hsiuhui Chang,QIAN LI,I Wai Sam',
    source: 'runtime', status: 1,
    last_order_at: '2026-11-03 21:06', created_at: '2026-11-03 21:10:33'
  },
  {
    id: 5, app_client_id: 62, app_client_name: '深圳道旅', target_type: 'li_cheng',
    mobile: '17117542394', guest_count: 8, order_count: 6,
    window_start: '2026-10-30', window_end: '2026-11-01',
    sample_names: 'Mohamad arif Bin che ismail,QIAN LI,I Wai Sam,Eng Xin Di',
    source: 'runtime', status: 1,
    last_order_at: '2026-11-03 18:42', created_at: '2026-11-03 18:50:12'
  },
  {
    id: 6, app_client_id: 19, app_client_name: 'yzp测试合作商', target_type: 'jin_jiang',
    mobile: '17118780627', guest_count: 5, order_count: 4,
    window_start: '2026-10-31', window_end: '2026-11-02',
    sample_names: '纪苗苗,王强,李伟,陈静',
    source: 'runtime', status: 0,
    last_order_at: '2026-11-02 16:20', created_at: '2026-11-02 16:25:40'
  }
]

export default {
  data() {
    return {
      list: [],
      total: 0,
      listLoading: false,
      listQuery: {
        page: 1,
        limit: 10,
        app_client_name: '',
        app_client_id: '',
        mobile: '',
        source: '',
        status: '',
        window_start_at: '',
        window_end_at: ''
      },
      dateArr: null,
      detailVisible: false,
      current: null,
      usingMock: false
    }
  },
  computed: {
    stats() {
      const rows = this.list
      return {
        total: this.total || rows.length,
        cold_start: rows.filter(r => r.source === 'cold_start').length,
        runtime: rows.filter(r => r.source === 'runtime').length,
        disabled: rows.filter(r => r.status === 0).length
      }
    }
  },
  watch: {
    dateArr(val) {
      if (val && val.length === 2) {
        this.listQuery.window_start_at = val[0]
        this.listQuery.window_end_at = val[1]
      } else {
        this.listQuery.window_start_at = ''
        this.listQuery.window_end_at = ''
      }
    }
  },
  created() {
    this.getList()
  },
  methods: {
    getList() {
      this.listLoading = true
      request({
        url: '/api/backend/agent-mobile/list',
        method: 'get',
        params: this.listQuery
      }).then(response => {
        this.list = (response.data && response.data.data) || []
        this.total = (response.data && response.data.total) || 0
        this.usingMock = false
      }).catch(() => {
        this.useMock()
      }).finally(() => {
        this.listLoading = false
      })
    },
    useMock() {
      this.usingMock = true
      let rows = MOCK.slice()
      const q = this.listQuery
      if (q.app_client_name) rows = rows.filter(r => (r.app_client_name || '').indexOf(q.app_client_name) > -1)
      if (q.app_client_id) rows = rows.filter(r => String(r.app_client_id) === String(q.app_client_id))
      if (q.mobile) rows = rows.filter(r => (r.mobile || '').indexOf(q.mobile) > -1)
      if (q.source) rows = rows.filter(r => r.source === q.source)
      if (q.status !== '' && q.status !== null) rows = rows.filter(r => r.status === q.status)
      this.total = rows.length
      const start = (q.page - 1) * q.limit
      this.list = rows.slice(start, start + q.limit)
    },
    handleFilter() {
      this.listQuery.page = 1
      this.getList()
    },
    handleReset() {
      this.dateArr = null
      this.listQuery = {
        page: 1, limit: 10, app_client_name: '', app_client_id: '', mobile: '',
        source: '', status: '',
        window_start_at: '', window_end_at: ''
      }
      this.getList()
    },
    handleSizeChange(val) {
      this.listQuery.limit = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.getList()
    },
    handleDetail(row) {
      this.current = row
      this.detailVisible = true
    },
    handleToggle(row, status) {
      const word = status === 1 ? '恢复启用' : '停用'
      this.$confirm(
        `确定要${word}手机号 ${row.mobile} 的代理号标记吗？`,
        `${word}确认`,
        { type: 'warning' }
      ).then(() => {
        request({
          url: '/api/backend/agent-mobile/status',
          method: 'post',
          data: { id: row.id, status: status }
        }).then(() => {
          row.status = status
          if (this.current && this.current.id === row.id) this.current.status = status
          this.$message.success(`${word}成功`)
        }).catch(() => {
          // 接口未就绪时本地生效，便于先看效果
          if (this.usingMock) {
            row.status = status
            if (this.current && this.current.id === row.id) this.current.status = status
            this.$message.warning('接口未就绪，已在本地模拟生效')
          }
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.agent-mobile-page {
  padding: 16px;
}

.stat-row {
  display: flex;
  margin-bottom: 12px;
}

.stat-card {
  flex: 1;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 14px 16px;
  margin-right: 12px;
}

.stat-card:last-child {
  margin-right: 0;
}

.stat-card--muted .stat-card__value {
  color: #909399;
}

.stat-card__label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.stat-card__value {
  font-size: 22px;
  font-weight: 500;
  color: #303133;
  line-height: 1.2;
}

.tip {
  margin-bottom: 12px;
}

.tip__body {
  font-size: 12px;
  color: #606266;
  line-height: 1.7;
  padding-top: 2px;
}

.filter-container {
  margin-bottom: 12px;
}

.filter-container .filter-item {
  margin-right: 8px;
  margin-bottom: 8px;
}

.mono {
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
}

.text-muted {
  color: #909399;
  font-size: 12px;
}

.text-danger {
  color: #f56c6c;
}

.pagination-wrap {
  margin-top: 14px;
  text-align: right;
}

.name-samples {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #ebeef5;
}

.name-samples__title {
  font-size: 13px;
  color: #303133;
  margin-bottom: 10px;
}

.name-samples__body {
  min-height: 24px;
}

.name-tag {
  margin-right: 6px;
  margin-bottom: 6px;
}

.name-samples__hint {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
  line-height: 1.7;
  background: #fafafa;
  border-radius: 6px;
  padding: 10px 12px;
}

@media (max-width: 768px) {
  .stat-row {
    flex-wrap: wrap;
  }
  .stat-card {
    flex: 1 0 45%;
    margin-right: 8px;
    margin-bottom: 8px;
  }
  .filter-container .filter-item {
    width: 100% !important;
    margin-right: 0;
  }
}
</style>
